import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { Button, DataTable, type DataTableColumn } from "@akuna/ui";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useCampaigns, type Campaign, type CampaignStatus } from "@/hooks/useCampaigns";
import { format } from "date-fns";

function StatusBadge({ status }: { status: CampaignStatus }) {
  const { t } = useTranslation("campaigns");
  const variantMap: Record<CampaignStatus, "default" | "secondary" | "destructive" | "outline"> = {
    completed: "default",
    scheduled: "secondary",
    dispatching: "secondary",
    draft: "outline",
    failed: "destructive",
    cancelled: "secondary",
  };
  const labelMap: Record<CampaignStatus, string> = {
    draft: t("statusDraft"),
    scheduled: t("statusScheduled"),
    dispatching: t("statusRunning"),
    completed: t("statusDone"),
    failed: t("statusFailed"),
    cancelled: t("statusPaused"),
  };
  return <Badge variant={variantMap[status]}>{labelMap[status]}</Badge>;
}

function MetricCell({ value, total }: { value: number; total: number }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <span className="tabular-nums">
      {value.toLocaleString()}
      {total > 0 && <span className="text-muted-foreground text-xs ml-1">({pct}%)</span>}
    </span>
  );
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat(undefined, { style: "currency", currency: "BRL" }).format(value);
}

function formatRoi(value: number | null): string {
  return value === null ? "—" : `${Math.round(value * 100)}%`;
}

export function CampaignsPage() {
  const { t } = useTranslation("campaigns");
  const navigate = useNavigate();
  const { data: campaigns = [], isLoading } = useCampaigns();

  const dataTableLabels = {
    columnsButton: t("dataTable.columns"),
    columnsTitle: t("dataTable.columnsTitle"),
    reset: t("dataTable.reset"),
    densityComfortable: t("dataTable.densityComfortable"),
    densityCompact: t("dataTable.densityCompact"),
    empty: t("dataTable.empty"),
  };

  const paginationLabels = {
    previous: t("dataTable.previous"),
    next: t("dataTable.next"),
  };

  const columns: DataTableColumn<Campaign>[] = [
    {
      id: "name",
      header: t("tableNameCol"),
      cell: (campaign) => <span className="font-medium">{campaign.name}</span>,
      sortAccessor: (campaign) => campaign.name,
      width: 220,
    },
    {
      id: "status",
      header: t("tableStatusCol"),
      cell: (campaign) => <StatusBadge status={campaign.status} />,
      sortAccessor: (campaign) => campaign.status,
      width: 150,
    },
    {
      id: "audience",
      header: t("tableAudienceCol"),
      cell: (campaign) => (
        <span className="tabular-nums text-sm">
          {campaign.estimatedRecipients?.toLocaleString() ?? "—"}
        </span>
      ),
      sortAccessor: (campaign) => campaign.estimatedRecipients ?? 0,
      align: "right",
      width: 140,
    },
    {
      id: "scheduled",
      header: t("tableScheduledCol"),
      cell: (campaign) => (
        <span className="text-sm">
          {campaign.scheduledAt ? format(new Date(campaign.scheduledAt), "dd/MM/yyyy HH:mm") : "—"}
        </span>
      ),
      sortAccessor: (campaign) => campaign.scheduledAt ?? "",
      width: 180,
    },
    {
      id: "sent",
      header: t("tableSentCol"),
      cell: (campaign) => <MetricCell value={campaign.totalSent} total={campaign.estimatedRecipients ?? 0} />,
      sortAccessor: (campaign) => campaign.totalSent,
      align: "right",
      width: 140,
    },
    {
      id: "delivered",
      header: t("tableDeliveredCol"),
      cell: (campaign) => <MetricCell value={campaign.totalDelivered} total={campaign.totalSent} />,
      sortAccessor: (campaign) => campaign.totalDelivered,
      align: "right",
      width: 150,
    },
    {
      id: "read",
      header: t("tableReadCol"),
      cell: (campaign) => <MetricCell value={campaign.totalRead} total={campaign.totalDelivered} />,
      sortAccessor: (campaign) => campaign.totalRead,
      align: "right",
      width: 130,
    },
    {
      id: "revenue",
      header: t("tableRevenueCol"),
      cell: (campaign) => (
        <span className="text-sm tabular-nums">
          {formatCurrency(campaign.attributedRevenue)}
          {campaign.conversions > 0 && (
            <span className="text-muted-foreground text-xs ml-1">
              ({t("tableConversions", { count: campaign.conversions })})
            </span>
          )}
        </span>
      ),
      sortAccessor: (campaign) => campaign.attributedRevenue,
      align: "right",
      width: 200,
    },
    {
      id: "roi",
      header: t("tableRoiCol"),
      cell: (campaign) => <span className="text-sm tabular-nums">{formatRoi(campaign.roi)}</span>,
      sortAccessor: (campaign) => campaign.roi ?? Number.NEGATIVE_INFINITY,
      align: "right",
      width: 110,
    },
  ];

  const suggestedCampaigns = [
    {
      key: "reactivateA",
      filter: { type: "segment", segment_filters: { abc_curve: ["A"], inactive_days: 90, has_whatsapp: true } },
    },
    {
      key: "overdueApplications",
      filter: { type: "segment", segment_filters: { overdue_vaccines: true, has_whatsapp: true } },
    },
    {
      key: "puppies",
      filter: { type: "segment", segment_filters: { detailed_age_group: "puppy", has_whatsapp: true } },
    },
    {
      key: "seniors",
      filter: { type: "segment", segment_filters: { detailed_age_group: "senior", has_whatsapp: true } },
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold">{t("pageTitle")}</h2>
          <p className="text-sm text-muted-foreground">{t("pageDesc")}</p>
        </div>
        <Button asChild size="sm" className="gap-1">
          <Link to="/reports/campaigns/new">
            <Plus className="h-4 w-4" />
            {t("newCampaign")}
          </Link>
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
        {suggestedCampaigns.map((suggestion) => (
          <Card key={suggestion.key}>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">{t(`suggested.${suggestion.key}.title`)}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="mb-3 text-xs text-muted-foreground">{t(`suggested.${suggestion.key}.desc`)}</p>
              <Button asChild variant="outline" size="sm" className="w-full">
                <Link to={`/reports/campaigns/new?filter=${encodeURIComponent(JSON.stringify(suggestion.filter))}`}>
                  {t("useSuggestion")}
                </Link>
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">{t("loadingCampaigns")}</p>
      ) : campaigns.length === 0 ? (
        <div className="rounded-lg border border-dashed p-10 text-center space-y-1">
          <p className="text-sm font-medium">{t("emptyState")}</p>
          <p className="text-xs text-muted-foreground">{t("emptyStateDesc")}</p>
        </div>
      ) : (
        <div className="rounded-lg border overflow-hidden">
          <DataTable
            columns={columns}
            data={campaigns}
            getRowId={(campaign) => campaign.id}
            labels={dataTableLabels}
            onRowClick={(campaign) => navigate(`/reports/campaigns/${campaign.id}`)}
            pageSize={10}
            paginationLabels={paginationLabels}
            persistKey="akuna:campaigns:table:v1"
            showColumnToggle
            showDensityToggle
          />
        </div>
      )}
    </div>
  );
}
